// Chat DTOs — clinic <-> lab threaded messaging (inbox style). A thread belongs to one clinic;
// staff see every thread, a clinic account sees only its own. Attachments are stored on disk and
// referenced by a message id (never inlined as blobs).
import { z } from 'zod';

/** Optional routing category for a thread (kept light; free-form is allowed too). */
export const CHAT_DEPARTMENTS = [
  { key: 'general', label: 'General' },
  { key: 'treatment', label: 'Treatment / Planning' },
  { key: 'records', label: 'Records / Scans' },
  { key: 'billing', label: 'Billing' },
  { key: 'dispatch', label: 'Dispatch' },
  { key: 'support', label: 'Support' },
] as const;
export type ChatDepartment = (typeof CHAT_DEPARTMENTS)[number]['key'];
export const CHAT_DEPARTMENT_KEYS = CHAT_DEPARTMENTS.map((d) => d.key) as [ChatDepartment, ...ChatDepartment[]];
export const CHAT_DEPARTMENT_LABELS: Record<string, string> = Object.fromEntries(
  CHAT_DEPARTMENTS.map((d) => [d.key, d.label]),
);

export type ChatSenderType = 'clinic' | 'staff' | 'patient' | 'system';
export type ChatThreadStatus = 'open' | 'closed';

export interface ChatMessage {
  id: string;
  threadId: string;
  senderId: string | null;
  senderType: ChatSenderType;
  senderName: string | null;
  body: string | null;
  attachmentName: string | null;
  attachmentMime: string | null;
  attachmentSize: number | null;
  hasAttachment: boolean;
  createdAt: string;
}

export interface ChatThread {
  id: string;
  clinicId: string | null;
  clinicName: string | null;
  subject: string | null;
  department: string | null;
  status: ChatThreadStatus;
  createdBy: string | null;
  createdByType: ChatSenderType;
  lastMessage: string | null;
  lastMessageAt: string | null;
  createdAt: string;
  updatedAt: string;
}

/** A thread row for the inbox: adds the current viewer's unread count. */
export interface ChatThreadListItem extends ChatThread {
  unread: number;
}

export interface ChatThreadDetail {
  thread: ChatThread;
  messages: ChatMessage[];
}

// --- request schemas ---------------------------------------------------------

export const chatCreateThreadSchema = z.object({
  /** Staff must name the clinic; a clinic account's own clinic is resolved server-side. */
  clinicId: z.string().trim().min(1).nullish(),
  subject: z.string().trim().min(1).max(160),
  department: z.enum(CHAT_DEPARTMENT_KEYS).nullish(),
  body: z.string().trim().max(4000).nullish(),
});
export type ChatCreateThreadRequest = z.infer<typeof chatCreateThreadSchema>;

export const chatSendMessageSchema = z.object({
  body: z.string().trim().max(4000).nullish(),
});
export type ChatSendMessageRequest = z.infer<typeof chatSendMessageSchema>;

export interface ChatUnreadResponse {
  total: number;
}
